import type { MaterialFormData, StopFormData } from '#components/order/CreateOrderDialog/OrderFormValidation.ts';
import { splitDateTimeValue } from '#components/order/CreateOrderDialog/timeWindowFields.ts';
import type { BulkCsvOrderRow, CargoCsvOrderRow, CsvOrderRow } from '#features/csvImport/CsvOrderRow.ts';
import type { ImportedOrder } from '#features/csvImport/ImportOrderFormValidation.ts';
import { parseBulkMaterial } from '#features/csvImport/parsing/bulkMaterialParser.ts';
import { parseCargoMaterial } from '#features/csvImport/parsing/cargoMaterialParser.ts';
import { parseCoordinates } from '#features/csvImport/parsing/coordinatesParser.ts';
import { parseDate } from '#features/csvImport/parsing/dateParser.ts';
import { parseNumber } from '#features/csvImport/parsing/utils.ts';
import { fetchHereGeocodePosition } from '#utils/here/fetchHereGeocodePosition.ts';

const getCoordinates = async ({
    coordinatesRaw,
    street,
    houseNumber,
    zipCode,
    city,
    country,
}: {
    coordinatesRaw: string;
    street: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    country: string;
}) => {
    const parsedCoordinates = parseCoordinates(coordinatesRaw);
    if (parsedCoordinates.latitude !== undefined && parsedCoordinates.longitude !== undefined) {
        return parsedCoordinates;
    }

    return fetchHereGeocodePosition({ street, houseNumber, zipCode, city, country });
};

const parseLoadingStop = async (raw: CsvOrderRow): Promise<StopFormData> => {
    const startDateTimeValue = parseDate({ date: raw.pickup_from_date, time: raw.pickup_from_time }) ?? '';
    const endDateTimeValue = parseDate({ date: raw.pickup_to_date, time: raw.pickup_to_time }) ?? '';
    const startTargetParts = splitDateTimeValue(startDateTimeValue);
    const endTargetParts = splitDateTimeValue(endDateTimeValue);
    const coordinates = await getCoordinates({
        coordinatesRaw: raw.pickup_coordinates,
        street: raw.pickup_street,
        houseNumber: raw.pickup_housenumber,
        city: raw.pickup_city,
        zipCode: raw.pickup_zip,
        country: raw.pickup_country,
    });
    return {
        type: 'load',
        name: raw.pickup_company,
        location: {
            street: [raw.pickup_street, raw.pickup_housenumber].filter(Boolean).join(' ').trim(),
            city: raw.pickup_city,
            country: raw.pickup_country,
            zipCode: raw.pickup_zip,
        },
        startDate: startTargetParts.date,
        startTime: startTargetParts.time,
        endDate: endTargetParts.date,
        endTime: endTargetParts.time,
        latitude: coordinates?.latitude ?? 0,
        longitude: coordinates?.longitude ?? 0,
    };
};

const parseUnloadingStop = async (raw: CsvOrderRow): Promise<StopFormData> => {
    const startDateTimeValue = parseDate({ date: raw.delivery_from_date, time: raw.delivery_from_time }) ?? '';
    const endDateTimeValue = parseDate({ date: raw.delivery_to_date, time: raw.delivery_to_time }) ?? '';
    const startTargetParts = splitDateTimeValue(startDateTimeValue);
    const endTargetParts = splitDateTimeValue(endDateTimeValue);
    const coordinates = await getCoordinates({
        coordinatesRaw: raw.delivery_coordinates,
        street: raw.delivery_street,
        houseNumber: raw.delivery_housenumber,
        city: raw.delivery_city,
        zipCode: raw.delivery_zip,
        country: raw.delivery_country,
    });
    return {
        type: 'unload',
        name: raw.delivery_company,
        location: {
            street: [raw.delivery_street, raw.delivery_housenumber].filter(Boolean).join(' ').trim(),
            city: raw.delivery_city,
            country: raw.delivery_country,
            zipCode: raw.delivery_zip,
        },
        startDate: startTargetParts.date,
        startTime: startTargetParts.time,
        endDate: endTargetParts.date,
        endTime: endTargetParts.time,
        latitude: coordinates?.latitude ?? 0,
        longitude: coordinates?.longitude ?? 0,
    };
};

export const parseBulkOrderRow = async (raw: BulkCsvOrderRow): Promise<ImportedOrder> => {
    const parsedCargo = parseBulkMaterial({
        quantity: parseNumber(raw.bulk_quantity),
        unitRaw: raw.bulk_quantity_unit,
        name: raw.bulk_name,
    });
    return parseOrderRow(raw, parsedCargo);
};

export const parseCargoOrderRow = async (raw: CargoCsvOrderRow): Promise<ImportedOrder> => {
    const parsedCargo = parseCargoMaterial({
        packageType: 'unspecified',
        quantity: parseNumber(raw.cargo_quantity),
        name: raw.cargo_name,
    });
    return parseOrderRow(raw, parsedCargo);
};

const parseOrderRow = async (raw: CsvOrderRow, material?: MaterialFormData): Promise<ImportedOrder> => {
    const [loadingStop, unloadingStop] = await Promise.all([parseLoadingStop(raw), parseUnloadingStop(raw)]);
    return {
        number: raw.order_number,
        customerName: raw.customer_name,
        loadingComment: raw.pickup_comment,
        unloadingComment: raw.delivery_comment,
        loadingStop,
        unloadingStop,
        materials: material ? [material] : [],
    };
};
